export type Dir = 'up' | 'down' | 'left' | 'right';

export type Car = { id: number; row: number; col: number; length: number; dir: Dir; colour: number };

export type Lot = { rows: number; cols: number; cars: Car[] };

export const COLOURS = 8;

export const STEP: Record<Dir, [number, number]> = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] };

export const across = (d: Dir) => d === 'left' || d === 'right';

export function cellsOf(car: Car): [number, number][] {
  const [dr, dc] = across(car.dir) ? [0, 1] : [1, 0];
  return Array.from({ length: car.length }, (_, k) => [car.row + dr * k, car.col + dc * k]);
}

function front(car: Car): [number, number] {
  const cells = cellsOf(car);
  return car.dir === 'up' || car.dir === 'left' ? cells[0] : cells[cells.length - 1];
}

function occupancy(lot: Lot, cars: Car[]): Int32Array {
  const grid = new Int32Array(lot.rows * lot.cols).fill(-1);
  for (const car of cars) for (const [r, c] of cellsOf(car)) grid[r * lot.cols + c] = car.id;
  return grid;
}

export type Path = { free: number; blocker: number | null; out: number };

export function pathOf(lot: Lot, cars: Car[], car: Car): Path {
  const grid = occupancy(lot, cars);
  const [dr, dc] = STEP[car.dir];
  let [r, c] = front(car);
  let free = 0;
  for (;;) {
    r += dr;
    c += dc;
    if (r < 0 || c < 0 || r >= lot.rows || c >= lot.cols) return { free, blocker: null, out: free };
    const at = grid[r * lot.cols + c];
    if (at !== -1) return { free, blocker: at, out: free };
    free++;
  }
}

export function canLeave(lot: Lot, cars: Car[], car: Car): boolean {
  return pathOf(lot, cars, car).blocker === null;
}

export function depth(lot: Lot, cars: Car[]): number | null {
  let left = cars;
  let rounds = 0;
  while (left.length > 0) {
    const go = left.filter((car) => canLeave(lot, left, car));
    if (go.length === 0) return null;
    const gone = new Set(go.map((c) => c.id));
    left = left.filter((c) => !gone.has(c.id));
    rounds++;
  }
  return rounds;
}

const DIRS: Dir[] = ['up', 'down', 'left', 'right'];

function attempt(rows: number, cols: number, fill: number, longShare: number): Car[] {
  const taken = new Uint8Array(rows * cols);
  const cars: Car[] = [];
  const target = Math.round(rows * cols * fill);
  let used = 0;
  for (let tries = 0; tries < 600 && used < target; tries++) {
    const dir = DIRS[Math.floor(Math.random() * 4)];
    const length = Math.random() < longShare ? 3 : 2;
    const h = across(dir);
    const row = Math.floor(Math.random() * (h ? rows : rows - length + 1));
    const col = Math.floor(Math.random() * (h ? cols - length + 1 : cols));
    const car: Car = { id: cars.length, row, col, length, dir, colour: Math.floor(Math.random() * COLOURS) };
    const cells = cellsOf(car);
    if (cells.some(([r, c]) => taken[r * cols + c])) continue;
    cells.forEach(([r, c]) => { taken[r * cols + c] = 1; });
    cars.push(car);
    used += length;
  }
  return cars;
}

export const SIZES: Record<number, { rows: number; cols: number; fill: number; longShare: number; minDepth: number }> = {
  5: { rows: 5, cols: 5, fill: 0.72, longShare: 0, minDepth: 3 },
  6: { rows: 6, cols: 6, fill: 0.78, longShare: 0.15, minDepth: 4 },
  8: { rows: 8, cols: 7, fill: 0.8, longShare: 0.2, minDepth: 5 },
};

export function makeLot(size: number): Lot {
  const spec = SIZES[size] ?? SIZES[6];
  let best: { cars: Car[]; score: number } | null = null;
  for (let i = 0; i < 400; i++) {
    const cars = attempt(spec.rows, spec.cols, spec.fill, spec.longShare);
    const lot = { rows: spec.rows, cols: spec.cols, cars };
    const d = depth(lot, cars);
    if (d === null) continue;
    const score = d * 10 + cars.length;
    if (!best || score > best.score) best = { cars, score };
    if (d >= spec.minDepth) break;
  }
  const cars = (best?.cars ?? []).map((c, id) => ({ ...c, id }));
  const owner = new Map<string, number>();
  cars.forEach((c) => cellsOf(c).forEach(([r, k]) => owner.set(`${r},${k}`, c.id)));
  for (const car of cars) {
    const near = new Set<number>();
    for (const [r, k] of cellsOf(car)) for (const [dr, dk] of [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [-1, 1], [1, -1], [1, 1]]) {
      const id = owner.get(`${r + dr},${k + dk}`);
      if (id !== undefined && id !== car.id && id < car.id) near.add(cars[id].colour);
    }
    const open = Array.from({ length: COLOURS }, (_, i) => (car.colour + i) % COLOURS).filter((c) => !near.has(c));
    if (open.length) car.colour = open[0];
  }
  return { rows: spec.rows, cols: spec.cols, cars };
}
